import { useMediaQuery } from '@vueuse/core'
import { computed, ref } from 'vue'

/**
 * Shared layout state for the auxiliary side pane: a second docked column (today
 * the Lottie chat) that can sit at the right edge of the app alongside the
 * primary slideover column managed by `SlideoverContainer`.
 *
 * Breakpoint rules mirror slideovers: at `(min-width: 1280px)` the pane docks
 * beside the page content, below that it falls back to a floating popover.
 * When the pane is docked and the remaining width cannot fit both the page and a
 * slideover, regular slideovers slide over the content instead of docking a
 * third column (see `useSlideoverStore`).
 *
 * This is a module-level singleton rather than an injected store because the
 * chat is mounted beside the Inertia app (see `app.js`), outside the component
 * tree that `SlideoverContainer` provides into.
 */

export const slideoverBreakpoint = '(min-width: 1280px)'

/** Narrowest the page content may get before docked columns shrink or overlay. */
export const minimumMainWidth = 384
/** Narrowest a docked slideover column may get. */
export const minimumSlideoverWidth = 384

export const defaultSidePaneWidth = 384
export const minimumSidePaneWidth = 320

let store = null

export function createSidePaneStore() {
  const isLargeScreen = useMediaQuery(slideoverBreakpoint)

  const sidePaneOpen = ref(false)
  const sidePaneWidth = ref(defaultSidePaneWidth)

  // Published by SlideoverContainer so the pane only docks when a layout can
  // make room for it, and so widths can be clamped to the available space.
  const slideoverContainerCount = ref(0)
  const containerWidth = ref(0)

  const sidePaneDocked = computed(
    () =>
      sidePaneOpen.value &&
      isLargeScreen.value &&
      slideoverContainerCount.value > 0
  )

  /** Horizontal space the docked pane takes from the right edge (0 when not docked). */
  const sidePaneOffset = computed(() =>
    sidePaneDocked.value ? sidePaneWidth.value : 0
  )

  // The pane only has to leave room for the page itself: a docked slideover
  // shrinks, then slides over, before the pane gives up width.
  const maximumSidePaneWidth = computed(() => {
    if (!containerWidth.value) return Infinity

    return Math.max(
      minimumSidePaneWidth,
      containerWidth.value - minimumMainWidth
    )
  })

  const setSidePaneWidth = (width) => {
    sidePaneWidth.value = Math.round(
      Math.min(
        Math.max(width, minimumSidePaneWidth),
        maximumSidePaneWidth.value
      )
    )
  }

  const registerSlideoverContainer = () => {
    slideoverContainerCount.value += 1

    return () => {
      slideoverContainerCount.value = Math.max(
        0,
        slideoverContainerCount.value - 1
      )
    }
  }

  return {
    isLargeScreen,
    sidePaneOpen,
    sidePaneWidth,
    sidePaneDocked,
    sidePaneOffset,
    maximumSidePaneWidth,
    setSidePaneWidth,
    containerWidth,
    slideoverContainerCount,
    registerSlideoverContainer,
  }
}

export function useSidePaneStore() {
  if (!store) store = createSidePaneStore()

  return store
}

/** Test helper: drop the singleton so the next `useSidePaneStore()` starts fresh. */
export function resetSidePaneStore() {
  store = null
}
